import {
  CheckCircle,
  Clock,
  Download,
  Info,
  Loader2,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";
import type { MouseEvent } from "react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui";
import { type RunOutputs, RunStatus, type UseCaseRun } from "@/types/content";
import { getRunImageUrls } from "@/utils/run";
import { formatDuration } from "@/views/Videos/utils";

const STATUS_LABEL: Record<RunStatus, string> = {
  [RunStatus.ACCEPTED]: "queued",
  [RunStatus.RUNNING]: "generating",
  [RunStatus.COMPLETED]: "ready",
  [RunStatus.FAILED]: "failed",
};

interface Props {
  run: UseCaseRun;
  outputs: RunOutputs;
  onSelect: () => void;
  onRegenerate: () => void;
  onDownload: (url: string) => Promise<void>;
  onDetails: () => void;
}

export const VideoCard = ({
  run,
  outputs,
  onSelect,
  onRegenerate,
  onDownload,
  onDetails,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isReady = run.status === RunStatus.COMPLETED;
  const isFailed = run.status === RunStatus.FAILED;
  const isActive = run.status === RunStatus.ACCEPTED || run.status === RunStatus.RUNNING;

  const videoUrl = outputs.video_url;
  const thumbnailUrl = outputs.thumbnail_url;
  const imageUrls = getRunImageUrls(outputs);
  const isCarousel = !videoUrl && imageUrls.length > 0;

  const startPreview = () => {
    if (!isReady || !videoUrl) return;
    setShowVideo(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const stopPreview = () => {
    setShowVideo(false);
    if (videoRef.current) videoRef.current.pause();
  };

  const handleDownloadClick = async (e: MouseEvent<HTMLButtonElement>, url: string) => {
    e.stopPropagation();
    setIsDownloading(true);
    await onDownload(url);
    setIsDownloading(false);
  };

  return (
    <div
      className="group cursor-pointer overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition-all hover:shadow-lg"
      onClick={onSelect}
    >
      <div
        className="relative aspect-4/3 bg-linear-to-br from-zinc-900 to-zinc-800"
        onMouseEnter={startPreview}
        onMouseLeave={stopPreview}
        onTouchStart={startPreview}
        onTouchEnd={stopPreview}
      >
        {isReady && isCarousel ? (
          imageUrls.length === 1 ? (
            <img
              src={imageUrls[0]}
              alt="Shot 1"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className={`absolute inset-0 grid gap-0.5 p-0.5 ${
                imageUrls.length === 2
                  ? "grid-cols-2"
                  : imageUrls.length === 3
                    ? "grid-cols-3"
                    : imageUrls.length === 4
                      ? "grid-cols-2 grid-rows-2"
                      : "grid-cols-3 grid-rows-2"
              }`}
            >
              {imageUrls.slice(0, 6).map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Shot ${i + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ))}
              {imageUrls.length > 6 && (
                <div className="flex items-center justify-center bg-black/60 font-bold text-sm text-white">
                  +{imageUrls.length - 6}
                </div>
              )}
            </div>
          )
        ) : isReady && videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={`${videoUrl}#t=0.5`}
              className="absolute inset-0 h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
              poster={thumbnailUrl || undefined}
            />
            {!showVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
                  <Play className="h-6 w-6 text-white" fill="white" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            {isActive ? (
              <Loader2 className="h-8 w-8 animate-spin text-white/40" />
            ) : (
              <XCircle className="h-8 w-8 text-red-400/60" />
            )}
          </div>
        )}
        <div className="absolute top-3 left-3">
          <div
            className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-semibold text-xs backdrop-blur-sm ${
              isReady
                ? "bg-emerald-500/90 text-white"
                : isActive
                  ? "bg-primary/90 text-white"
                  : "bg-red-500/90 text-white"
            }`}
          >
            {isReady ? (
              <CheckCircle className="h-3 w-3" />
            ) : isActive ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <XCircle className="h-3 w-3" />
            )}
            {STATUS_LABEL[run.status as RunStatus]}
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="truncate font-semibold text-sm text-zinc-800">
              {run.task_type_label ?? run.task_type_id}
              {isCarousel && imageUrls.length ? ` · ${imageUrls.length} photos` : ""}
            </p>
            <p className="mt-0.5 text-xs text-zinc-400">
              {new Date(run.created_at).toLocaleDateString()}
            </p>
          </div>
          {isReady && (
            <div className="flex gap-1.5">
              <Button
                variant="secondary"
                size="icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRegenerate();
                }}
                title="Regenerate"
              >
                <RefreshCw />
              </Button>
              {videoUrl && (
                <Button
                  variant="secondary"
                  size="icon-sm"
                  onClick={(e) => handleDownloadClick(e, videoUrl)}
                  disabled={isDownloading}
                  title="Download"
                >
                  {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
                </Button>
              )}
            </div>
          )}
        </div>

        {isReady && run.duration_seconds != null && (
          <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(run.duration_seconds)}
            </span>
          </div>
        )}

        <div className="mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDetails();
            }}
            className="text-zinc-500 hover:text-zinc-600"
          >
            <Info />
            Details
          </Button>
        </div>

        {isFailed && run.error && (
          <p className="mt-2 text-red-500 text-xs">{run.error}</p>
        )}
      </div>
    </div>
  );
};
